<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'
import { bbox } from '@turf/turf'
import { FolderDot, ChevronDown, Eye, EyeOff, ZoomIn } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ImagesList } from '@/components/ui/images'
import { Badge } from '@/components/ui/badge'
import { Point, LineString, Polygon } from '@/components/icons'
import { API, HOST } from '@/utils/http'
import { formatTime, formatTimeFromNow } from '@/utils/datetime'

const props = defineProps({
  feature: Object
})

const FeaturesListChild = defineAsyncComponent(() => import("@/components/features/FeaturesList.vue"))

const isShowChilds = ref(false)
const isDateToggle = ref(true)
const currentFeatureChilds = ref(null)

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const settings = computed(() => store.getters['user/getUserSettings'])
const currentFeature = computed(() => store.getters['geo/getCurrentFeature'])

const isCurrent = computed(() => {
  if (!currentFeature.value) return false
  return currentFeature.value.properties.code === props.feature.properties.code
})

const isProjectBoundary = computed(() => props.feature.properties.paired_project_id != null)

const isVisible = computed(() => {
  return store.getters['geo/getFeatureVisibility'](props.feature.properties.code)
})

const toggleVisibility = () => {
  store.commit('geo/TOGGLE_FEATURE_VISIBILITY', props.feature.properties.code)
}

const fitToGeometry = () => {
  if (!map.value || !props.feature) return

  try {
    const featureBbox = bbox(props.feature)
    map.value.fitBounds(featureBbox, {
      padding: 100,
      duration: 500
    })
  } catch (error) {
    console.error('Error zooming to feature:', error)
    // Fallback to center point if bbox fails
    if (props.feature.geometry?.type === 'Point') {
      const [lng, lat] = props.feature.geometry.coordinates
      map.value.flyTo({
        center: [lng, lat],
        zoom: 16,
        duration: 500
      })
    }
  }
}

const iconName = computed(() => {
  const subtype = props.feature.properties.subtype ? props.feature.properties.subtype : 'report'
  const geometryType = props.feature.geometry.type.toLowerCase()
  return subtype === 'report' ? geometryType : subtype
})

const resources = computed(() => {
  const r = props.feature.properties.resources || []
  const thumbnails = r.filter((i) => i.type === 'thumbnail')
  const fullsizes = r.filter((i) => i.type === 'fullsize')
  return { thumbnails, fullsizes }
})

const resourcesLength = computed(() => resources.value.thumbnails.length)

const createdAt = computed(() => {
  let created_origin = props.feature.properties.created_origin
  if (!created_origin) return props.feature.properties.created_at
  if (created_origin.toString().length === 10) {
    created_origin = created_origin * 1000
  }
  return created_origin
})

const createdAtFormated = computed(() => formatTime(createdAt.value, settings.value.tz, 'D MMMM YYYY HH:mm'))
const createdAtFromNow = computed(() => formatTimeFromNow(createdAt.value, settings.value.tz))

async function fetchFeatureChilds() {
  const response = await API({
    method: 'GET',
    url: `${HOST}/geo/feature/${props.feature.properties.code}/childs`
  })
  currentFeatureChilds.value = response.data
}
</script>

<template>
  <div class="border-b" :class="{'': isCurrent}">
    <div style="overflow: hidden;">
      <div class="px-3 py-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <Avatar class="w-5 h-5 mr-2">
              <AvatarImage
                v-if="props.feature.properties.member_avatar_url"
                :src="props.feature.properties.member_avatar_url"
                :alt="props.feature.properties.member_display_name"
                :key="props.feature.properties.code"
              />
              <AvatarFallback />
            </Avatar>
            <span class="text-gray-500">{{ props.feature.properties.member_display_name }}</span>
          </div>
          <div @click.stop="isDateToggle = !isDateToggle">
            <span v-if="isDateToggle" class="text-gray-500">{{ createdAtFromNow }}</span>
            <span v-else class="text-gray-500">{{ createdAtFormated }}</span>
          </div>
        </div>
        <div class="mt-3 flex">
          <div
            class="min-w-8 min-h-8 w-8 h-8 rounded-full shadow-sm flex items-center justify-center"
            :style="{'background-color': props.feature.properties.color}"
          >
            <div v-if="props.feature.properties.s3_icon">
              <img class="object-contain w-5 h-5" :src="props.feature.properties.s3_icon" alt="" />
            </div>
            <div v-else>
              <Point v-if="iconName === 'point'" class="fill-white" />
              <LineString v-if="iconName === 'linestring'" class="fill-white" />
              <Polygon v-if="iconName === 'polygon'" class="fill-white" />
            </div>
          </div>
          <div class="ml-3 mt-1">
            <router-link :to="{ name: 'Feature', params: { featureCode: props.feature.properties.code } }">
              <h3 class="text-base font-semibold hover:text-primary">
                <Badge v-if="isProjectBoundary" variant="outline" class="mr-2 text-xs">Project Boundary</Badge>
                <span v-if="!isProjectBoundary">{{ props.feature.properties.name }}</span>
              </h3>
            </router-link>
          </div>
        </div>

        <div v-if="props.feature.properties.resources && resourcesLength" class="mt-3">
          <ImagesList :images="resources" />
        </div>

        <div class="flex items-center justify-between mt-3">
          <div class="flex items-center justify-end">
            <Button
              @click.stop="fitToGeometry"
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              title="Fit to geometry"
            >
              <ZoomIn class="h-4 w-4 text-gray-500" />
            </Button>
            <Button
              @click.stop="toggleVisibility"
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              :title="isVisible ? 'Hide on map' : 'Show on map'"
            >
              <Eye v-if="isVisible" class="h-4 w-4 text-gray-500" />
              <EyeOff v-else class="h-4 w-4 text-gray-400" />
            </Button>
          </div>
          <div v-if="props.feature.properties.shared" class="flex items-center justify-between">
            <Badge variant="secondary" class="text-xs">Public</Badge>
          </div>
        </div>
      </div>
    </div>

    <div v-if="props.feature.properties.childs_count > 0" class="px-3 pb-3">
      <Collapsible v-model:open="isShowChilds">
        <CollapsibleTrigger>
          <Button size="sm" variant="outline" @click.stop="fetchFeatureChilds" class="flex items-center text-gray-600">
            {{ props.feature.properties.childs_count }} reports
            <ChevronDown class="ml-1 w-4 h-4 text-gray-400" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div class="border border-gray-200 mt-2 rounded-lg">
            <FeaturesListChild
              v-if="currentFeatureChilds"
              :features="currentFeatureChilds?.features"
              is-child
              :key="props.feature.properties.code"
              no-pagination
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  </div>
</template>
